import {
  Container,
  Skeleton,
  Stack,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { CashManagementHeaderBreadcrumbs } from './CashOverviewPage';
import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addMissingFactorsAndSort,
  useCashTransactionGroupPhoenix as useCashTransactionGroup,
  useCashTransactionGroupAddTransaction,
} from '@pflegenavi/frontend/api-nursing-home';
import type { Coin } from '@pflegenavi/shared/api';
import type { TransformedCashTransactionGroup } from '@pflegenavi/frontend/api-nursing-home';
import {
  CashManagementTransactionType,
  CashTransactionGroupType,
  FeatureFlag,
} from '@pflegenavi/shared/api';
import {
  Button,
  DropdownSplitButton,
  Iconify,
  Modal,
  ModalInner,
} from '@pflegenavi/web-components';
import { ChangeCashMode } from './components/form/model';
import { ChangeCashForm } from './components/form/ChangeCashForm';
import { UNRESTRICTED_CASH_LIST_RULES_BY_MODE } from './changeCashSelectorRules';
import { ReceiptTable } from './components/groupDetail/ReceiptTable';
import { GroupOverview } from './components/groupDetail/GroupOverview';
import { CashTransactionsTable } from './components/groupDetail/CashTransactionsTable';
import { TransactionsTable } from './components/groupDetail/TransactionsTable';
import { ReceiptBatchTable } from './components/groupDetail/ReceiptBatchTable';
import { PendingChargesTable } from './components/groupDetail/PendingChargesTable';
import { PayoutsTable } from './components/groupDetail/PayoutsTable';
import { LinkedTransfersTable } from './components/groupDetail/LinkedTransfersTable';
import { ExpenseTable } from './components/groupDetail/ExpenseTable';
import { AssignToExpenseManagementModal } from './components/expenseManagement/AssignToExpenseManagementModal';
import {
  isAssignToExpenseEligible,
  isCreateReceiptOrBatchEligible,
} from './components/expenseManagement/eligibility';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import {
  useEmployeeIsNursingHomeAdminOrManager,
  useNursingHomeContext,
} from '@pflegenavi/frontend/nursing-home-context';

interface AddCashTransactionModalProps {
  open: boolean;
  onClose: () => void;
  type: ChangeCashMode;

  onAddCashTransactionGroup: (
    type: ChangeCashMode,
    coins: Coin[],
    bankAccountAmount?: number,
    notes?: string
  ) => void;
  cashListId: string;
}

export const AddCashTransactionModal: FC<AddCashTransactionModalProps> = ({
  open,
  onClose,
  type,
  onAddCashTransactionGroup,
  cashListId,
}) => {
  const { selectedNursingHome } = useNursingHomeContext();

  return (
    <Modal open={open} handleClose={onClose}>
      <ModalInner>
        <ChangeCashForm
          forceCashListId={cashListId}
          enableWithdrawalReceiptDownload={true}
          selectorRulesByMode={UNRESTRICTED_CASH_LIST_RULES_BY_MODE}
          requireNote={false}
          handleClose={onClose}
          accountingPaymentsFeatureFlag={true}
          initialValues={{
            mode: type,
            step: 1,
          }}
          onConfirm={(data) =>
            Promise.resolve(
              onAddCashTransactionGroup(
                type,
                data.coins,
                data.bankAccountAmount,
                data.notes
              )
            )
          }
          mandatoryCashLink={
            selectedNursingHome?.legalEntity
              ?.mandatory_cash_link_for_receipts ?? false
          }
        />
      </ModalInner>
    </Modal>
  );
};

const CashTransactionGroupDetailPage: FC = () => {
  const { groupId } = useParams();
  const { t } = useTranslation();
  const { data } = useCashTransactionGroup(groupId);
  const title = t('cashManagement.group-detail.overview.title');

  return (
    <Container maxWidth={'lg'}>
      <CashManagementHeaderBreadcrumbs
        heading={title}
        pageTitle={title}
        action={data ? <GroupHeaderActions group={data} /> : undefined}
        hideActionButton={true}
      />

      <CashTransactionGroupDetailContent />
    </Container>
  );
};

const CashTransactionGroupDetailContent: FC = () => {
  const { groupId } = useParams();
  // HeaderBreadcrumbs hides its action on small screens
  const smallScreens = useMediaQuery('(max-width:900px)');

  const { data, isLoading } = useCashTransactionGroup(groupId);

  const cashTransactions = data?.cashListTransactions ?? [];

  const showDownloadPdf = useCallback(
    (transaction: { type: CashManagementTransactionType }) =>
      data?.type === CashTransactionGroupType.ReceiptWithdrawal &&
      transaction.type === CashManagementTransactionType.Withdrawal,
    [data?.type]
  );

  if (isLoading || !data || !groupId) {
    return (
      <Stack
        direction="column"
        sx={{
          gap: 3,
          width: 'fit-content',
        }}
      >
        <Skeleton variant="rectangular" height={400} width="100%" />
      </Stack>
    );
  }

  const { linked } = data;
  const payoutCount =
    linked.nursingHomePayoutIds.length + linked.stripePayoutCount;

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Stack
        sx={{
          gap: 2,
        }}
      >
        {smallScreens && (
          <Stack
            direction="row"
            sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2 }}
          >
            <GroupHeaderActions group={data} />
          </Stack>
        )}

        <GroupOverview
          type={data.type}
          lastUpdateDate={data.updateDate}
          cashList={data.cashList}
          cashAmount={data.cashChanged ?? 0}
          separateFeeAmount={data.separateFeeAmount ?? 0}
          immediateFeeAmount={data.immediateFeeAmount ?? 0}
          linkedAmount={data.amountLinked}
          linkedSeparateFeeAmount={data.separateFeeAmountLinked}
          linkedImmediateFeeAmount={data.immediateFeeAmountLinked}
          expensePartCount={data.expensePartCount}
        >
          {cashTransactions.length > 0 && (
            <CashTransactionsTable
              transactions={cashTransactions}
              showDownloadPdf={showDownloadPdf}
            />
          )}
        </GroupOverview>
      </Stack>
      {data.receiptLinkCount > 0 && (
        <ReceiptTable
          variant="linked"
          groupId={groupId}
          total={data.receiptLinkCount}
        />
      )}
      {linked.settledReceiptIds.length > 0 && (
        <ReceiptTable variant="settled" ids={linked.settledReceiptIds} />
      )}
      {(data.transactions?.length ?? 0) > 0 && (
        <TransactionsTable transactions={data.transactions ?? []} />
      )}
      {linked.openReceiptBatchIds.length > 0 && (
        <ReceiptBatchTable variant="open" ids={linked.openReceiptBatchIds} />
      )}
      {linked.submittedReceiptBatchIds.length > 0 && (
        <ReceiptBatchTable
          variant="submitted"
          ids={linked.submittedReceiptBatchIds}
        />
      )}
      {linked.pendingChargeIds.length > 0 && (
        <PendingChargesTable chargeIds={linked.pendingChargeIds} />
      )}
      {payoutCount > 0 && (
        <PayoutsTable
          ids={linked.nursingHomePayoutIds}
          stripePayoutCount={linked.stripePayoutCount}
        />
      )}
      {data.linkedCashListTransactions.length > 0 && (
        <LinkedTransfersTable transfers={data.linkedCashListTransactions} />
      )}
      {data.expensePartCount > 0 && <ExpenseTable groupId={groupId} />}
    </Stack>
  );
};

export default CashTransactionGroupDetailPage;

const GroupHeaderActions: FC<{ group: TransformedCashTransactionGroup }> = ({
  group,
}) => {
  const employeeIsNursingHomeAdminOrManager =
    useEmployeeIsNursingHomeAdminOrManager();
  const hasCashTransactions = (group.cashListTransactions?.length ?? 0) > 0;

  return (
    <Stack
      direction="row"
      sx={{ gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}
    >
      <GroupActionButtons group={group} />
      {employeeIsNursingHomeAdminOrManager && hasCashTransactions && (
        <WithdrawalDepositButtons group={group} />
      )}
    </Stack>
  );
};

const WithdrawalDepositButtons: FC<{
  group: TransformedCashTransactionGroup;
}> = ({ group }) => {
  const { t } = useTranslation();

  const { selectedNursingHome } = useNursingHomeContext();

  const { mutateAsync: addTransaction } = useCashTransactionGroupAddTransaction(
    selectedNursingHome?.id
  );

  const [cashLinkOpen, setCashLinkOpen] = useState<undefined | ChangeCashMode>(
    undefined
  );

  if (
    group.type === CashTransactionGroupType.Transfer ||
    group.type === CashTransactionGroupType.Adjustment
  ) {
    return null;
  }

  const buttonDisabled =
    !group.manualTransactionCreationAllowed ||
    group.reversed ||
    group.type === CashTransactionGroupType.ResidentDeposit ||
    group.cashListTransactions?.some(({ fullReversal }) => fullReversal);
  // The group's own direction is the likely next transaction
  const orderedModes =
    group.cashChanged < 0
      ? [ChangeCashMode.Withdraw, ChangeCashMode.Deposit]
      : [ChangeCashMode.Deposit, ChangeCashMode.Withdraw];
  return (
    <>
      <DropdownSplitButton
        width="fit-content"
        disabled={buttonDisabled}
        options={orderedModes.map((mode) => ({
          label: t(
            mode === ChangeCashMode.Withdraw
              ? 'cashManagement.link.add-withdrawal'
              : 'cashManagement.link.add-deposit'
          ),
          onClick: () => setCashLinkOpen(mode),
        }))}
      />
      <AddCashTransactionModal
        key={(cashLinkOpen ?? 'closed').toString()}
        cashListId={group.cashListId}
        open={Boolean(cashLinkOpen)}
        onClose={() => setCashLinkOpen(undefined)}
        type={
          cashLinkOpen === 'withdraw'
            ? ChangeCashMode.Withdraw
            : ChangeCashMode.Deposit
        }
        onAddCashTransactionGroup={(type, coins, bankAccountAmount, notes) => {
          const coinsSorted =
            coins !== undefined && coins.length > 0
              ? addMissingFactorsAndSort(coins).map((coin) => coin.amount)
              : undefined;
          addTransaction({
            cashTransactionGroupId: group.id,
            type: type === ChangeCashMode.Withdraw ? 'withdrawal' : 'deposit',
            coins: coinsSorted,
            bankAccountAmount: bankAccountAmount,
            notes,
          });
        }}
      />
    </>
  );
};

const GroupActionButtons: FC<{
  group: TransformedCashTransactionGroup;
}> = ({ group }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );
  const receiptManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ReceiptManagement
  );

  const [assignOpen, setAssignOpen] = useState(false);

  const expenseExists = (group.expensePartCount ?? 0) > 0;

  // Assign-to-expense is shown only when the group is fully eligible (withdrawal
  // group, has an amount, nothing linked, no expense yet); otherwise hidden.
  const assignEligible = isAssignToExpenseEligible({
    type: group.type,
    amountLinkedCents: group.amountLinked,
    cashChangedCents: group.cashChanged,
    expensePartCount: group.expensePartCount,
  });

  const createReceiptEligible = isCreateReceiptOrBatchEligible({
    type: group.type,
    cashChangedCents: group.cashChanged,
    expensePartCount: group.expensePartCount,
  });

  // Open amount still to be linked. The receipt form expects positive cents,
  // so prefill with absolute values (deposit groups have positive amounts).
  const openAmount = group.cashChanged - group.amountLinked;
  // Receipt buttons are hidden once an expense exists; while shown, hint when
  // the group is already linked to a matching amount (no open amount left).
  const receiptTooltip =
    openAmount === 0
      ? t('cashManagement.group-detail.actions.already-matched-tooltip')
      : '';

  const handleCreateReceipt = (batch: boolean) => {
    const queryParams = new URLSearchParams();
    queryParams.set('cashTransactionGroupId', group.id);
    queryParams.set('receiptAmount', Math.abs(openAmount).toString());
    queryParams.set(
      'additionalFeeAmount',
      Math.abs(
        group.separateFeeAmount - group.separateFeeAmountLinked
      ).toString()
    );
    queryParams.set(
      'includedFeeAmount',
      Math.abs(
        group.immediateFeeAmount - group.immediateFeeAmountLinked
      ).toString()
    );
    queryParams.set('date', new Date(group.updateDate).toISOString());
    if (batch) {
      queryParams.set('cashListId', group.cashListId);
      navigate(
        `${nhAppMainPages.RECEIPTS_BATCH_ADD}?${queryParams.toString()}`
      );
    } else {
      navigate(
        `${
          nhAppMainPages.TRANSACTIONS_ADD_EMPTY_RESIDENT
        }?${queryParams.toString()}`
      );
    }
  };

  const showAssign = expenseManagementEnabled && assignEligible;
  const showReceiptButtons =
    receiptManagementEnabled && !expenseExists && createReceiptEligible;
  if (!showAssign && !showReceiptButtons) {
    return null;
  }

  return (
    <>
      {showAssign && (
        <Button
          variant="outlined"
          onClick={() => setAssignOpen(true)}
          startIcon={<Iconify icon="tabler:receipt-euro" />}
        >
          {t('cashManagement.actions.assign-to-expense')}
        </Button>
      )}
      {showReceiptButtons && (
        <Tooltip title={receiptTooltip} arrow>
          <span>
            <DropdownSplitButton
              variant="outlined"
              width="fit-content"
              disabled={openAmount === 0}
              options={[
                {
                  label: t('receipts.unassociatedPliant.addReceipt'),
                  onClick: () => handleCreateReceipt(false),
                },
                {
                  label: t('receipts.unassociatedPliant.addReceiptBatch'),
                  onClick: () => handleCreateReceipt(true),
                },
              ]}
            />
          </span>
        </Tooltip>
      )}
      {assignOpen && (
        <AssignToExpenseManagementModal
          open
          onClose={() => setAssignOpen(false)}
          cashTransactionGroupId={group.id}
          groupAmountCents={Math.abs(group.cashChanged ?? 0)}
          pliantTransactionCreatedAt={group.pliantTransactionCreatedAt?.toISOString()}
        />
      )}
    </>
  );
};
